import { Box, Button, Dialog, DialogActions, DialogTitle, DialogContent, IconButton, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import React, { useMemo } from 'react';
import { styles } from './FoldersAndReportsActionErrorsDialog.styles';
import { ItemsNames, useFoldersContext } from '@opengov/bnp-component-folders/src';
import { IFolder, EReportsListV4ItemType, IReportV4 } from '@opengov/reporting-types';
import groupBy from 'lodash/groupBy';
import { getFoldersAndReportsPhrase } from '../../ReportsPage.utils';
import { ROOT_FOLDER } from '../ReportsFolders/ReportsFolders.utils';
import {
  ACTION_ERROR_CODES,
  FoldersAndReportsActionPayload,
  getExtendedActionErrors,
  getRetryIds,
  ItemsActionResponse
} from './FoldersAndReportsActionErrorsDialog.utils';

const intl = {
  title: 'Some Errors Occurred',
  cancel: 'Cancel',
  retry: (count: number) => (count === 0 ? `Retry` : `Retry ${count === 1 ? 'Item' : `${count} Items`}`),
  closeIconAriaLabel: 'close button for dialog',
  location: 'Location',
  unexpectedError: 'unexpected error. Please retry.',
  codeToPhrase: {
    [ACTION_ERROR_CODES.NOT_FOUND_OR_PERMISSION_DENIED]: 'restricted permissions or absence.',
    [ACTION_ERROR_CODES.FOLDERS_NESTING_EXCEEDS_7_LEVELS]: 'folders nesting exceeding 7 levels.'
  }
};

type FoldersAndReportsActionErrorsDialogProps = {
  title?: string;
  unexpectedError?: string;
  codeToPhrase?: typeof intl.codeToPhrase;
  showLocation?: boolean;
  folders: IFolder[];
  reports: IReportV4[];
  failedActionResponse: ItemsActionResponse['failed'] & { parentFolderId: string };
  isRetrying: boolean;
  onClose: () => void;
  onRetry: (args: FoldersAndReportsActionPayload) => Promise<void>;
};

export function FoldersAndReportsActionErrorsDialog({
  title = intl.title,
  unexpectedError = intl.unexpectedError,
  codeToPhrase = intl.codeToPhrase,
  showLocation = false,
  folders,
  reports,
  failedActionResponse,
  isRetrying,
  onClose,
  onRetry
}: FoldersAndReportsActionErrorsDialogProps) {
  const { foldersMap } = useFoldersContext();
  const folderExtendedActionErrors = useMemo(
    () => getExtendedActionErrors(failedActionResponse.folders, failedActionResponse.parentFolderId, folders),
    [failedActionResponse.folders, failedActionResponse.parentFolderId, folders]
  );
  const reportExtendedActionErrors = useMemo(
    () => getExtendedActionErrors(failedActionResponse.reports, failedActionResponse.parentFolderId, reports),
    [failedActionResponse.reports, failedActionResponse.parentFolderId, reports]
  );
  const { folderIds, reportIds, total } = useMemo(() => {
    const folderIds = getRetryIds(folderExtendedActionErrors);
    const reportIds = getRetryIds(reportExtendedActionErrors);
    return { folderIds, reportIds, total: folderIds.length + reportIds.length };
  }, [folderExtendedActionErrors, reportExtendedActionErrors]);
  const extendedActionErrors = useMemo(() => {
    return groupBy([...folderExtendedActionErrors, ...reportExtendedActionErrors], 'code');
  }, [folderExtendedActionErrors, reportExtendedActionErrors]);

  const handleRetry = () => {
    void onRetry({
      sourceFolderIds: folderIds,
      sourceReportIds: reportIds,
      destinationFolderId: failedActionResponse.parentFolderId
    });
  };

  let locationTitle = ROOT_FOLDER.title;
  if (failedActionResponse.parentFolderId && failedActionResponse.parentFolderId !== ROOT_FOLDER.id) {
    locationTitle = foldersMap.get(failedActionResponse.parentFolderId)?.title ?? '';
  }

  return (
    <Dialog open={true} onClose={onClose} fullWidth data-test="foldersAndReportsActionErrorsDialog">
      <Box sx={styles.header}>
        <DialogTitle sx={styles.title} data-test="foldersAndReportsActionErrorsDialogTitle">
          <ErrorIcon />
          {title}
        </DialogTitle>
        <IconButton sx={styles.closeIcon} aria-label={intl.closeIconAriaLabel} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={styles.content}>
        {showLocation ? (
          <Box data-test="locationTitleContainer">
            <Typography sx={styles.label} component="label" htmlFor="locationTitle">
              {intl.location}
            </Typography>
            <Typography id="locationTitle" noWrap={true} component="div">
              {locationTitle}
            </Typography>
          </Box>
        ) : null}
        {Object.keys(extendedActionErrors).map((code) => {
          const folders = extendedActionErrors[code].filter(({ type }) => type === EReportsListV4ItemType.FOLDER);
          const reports = extendedActionErrors[code].filter(
            ({ type }) =>
              type === EReportsListV4ItemType.ACCOUNT_SUMMARY_REPORT ||
              type === EReportsListV4ItemType.CHART_AND_DETAILS_REPORT
          );
          const foldersAndReportsPhrase = getFoldersAndReportsPhrase({
            foldersCount: folders.length,
            reportsCount: reports.length
          });
          return (
            <ItemsNames
              alwaysShowAccordion={true}
              accordionTitle={`${foldersAndReportsPhrase} ${codeToPhrase[code] ?? unexpectedError}`}
              key={code}
              items={extendedActionErrors[code]}
            />
          );
        })}
      </DialogContent>

      <DialogActions>
        <Button disabled={isRetrying} color="secondary" data-test="cancelActionButton" onClick={onClose}>
          {intl.cancel}
        </Button>

        <LoadingButton
          loading={isRetrying}
          disabled={isRetrying}
          color="primary"
          variant="contained"
          data-test="retryActionButton"
          startIcon={<AutorenewIcon />}
          onClick={handleRetry}
        >
          {intl.retry(total)}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
